import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, Clock, CheckCircle, Truck, AlertCircle,
  Loader2, Upload, Link as LinkIcon, ExternalLink, Trash2,
  FileText, Image, File, DollarSign, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { ordersAPI } from '../utils/api';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type icons
const getFileIcon = (fileType) => {
  if (!fileType) return File;
  const ext = fileType.toLowerCase();
  if (['.pdf'].includes(ext)) return FileText;
  if (['.jpg', '.jpeg', '.png', '.svg', '.tiff', '.tif'].includes(ext)) return Image;
  return File;
};

// Status badge
const StatusBadge = ({ status, type = 'order' }) => {
  const orderStatuses = {
    pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
    processing: { label: 'Processing', className: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
    shipped: { label: 'Shipped', className: 'bg-purple-100 text-purple-800 border-purple-200', icon: Truck },
    delivered: { label: 'Delivered', className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
  };
  
  const paymentStatuses = {
    pending: { label: 'Pending Payment', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    pending_payment: { label: 'Pay Later', className: 'bg-amber-100 text-amber-800 border-amber-200' },
    paid: { label: 'Paid', className: 'bg-green-100 text-green-800 border-green-200' },
    failed: { label: 'Payment Failed', className: 'bg-red-100 text-red-800 border-red-200' },
  };
  
  const artworkStatuses = {
    pending: { label: 'Artwork Pending', className: 'bg-gray-100 text-gray-700 border-gray-200' },
    uploaded: { label: 'Artwork Uploaded', className: 'bg-blue-100 text-blue-800 border-blue-200' },
    approved: { label: 'Artwork Approved', className: 'bg-green-100 text-green-800 border-green-200' },
    rejected: { label: 'Needs Revision', className: 'bg-red-100 text-red-800 border-red-200' },
  };

  const statuses = type === 'payment' ? paymentStatuses : type === 'artwork' ? artworkStatuses : orderStatuses;
  const config = statuses[status] || statuses.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.className}`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {config.label}
    </span>
  );
};

const CustomerOrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingItem, setUploadingItem] = useState(null);
  const [linkInputs, setLinkInputs] = useState({});

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: `/my-orders/${orderId}` } });
    }
  }, [authLoading, isAuthenticated, navigate, orderId]);

  // Fetch order
  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await ordersAPI.get(orderId);
      setOrder(data);
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // Handle file upload for a line item
  const handleFileUpload = async (lineItemId, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['.pdf', '.ai', '.eps', '.svg', '.png', '.jpg', '.jpeg', '.tiff', '.tif', '.psd'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast({
        title: "Invalid file type",
        description: `Allowed: ${allowedTypes.join(', ')}`,
        variant: "destructive"
      });
      return;
    }
    
    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 50MB",
        variant: "destructive"
      });
      return;
    }
    
    setUploadingItem(lineItemId);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/uploads/line-item/${orderId}/${lineItemId}/artwork`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      toast({
        title: "Artwork uploaded",
        description: "Your artwork has been uploaded successfully",
      });
      
      fetchOrder(); // Refresh order to show new artwork
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setUploadingItem(null);
    }
  };

  // Handle link submission for a line item
  const handleLinkSubmit = async (lineItemId) => {
    const url = linkInputs[lineItemId];
    if (!url) return;
    
    // Basic URL validation
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid URL starting with http:// or https://",
        variant: "destructive"
      });
      return;
    }
    
    setUploadingItem(lineItemId);
    
    try {
      const formData = new FormData();
      formData.append('link_url', url);
      
      const response = await fetch(`${API_URL}/api/uploads/line-item/${orderId}/${lineItemId}/artwork-link`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Submission failed');
      }
      
      toast({
        title: "Link submitted",
        description: "Your artwork link has been submitted",
      });
      
      setLinkInputs(prev => ({ ...prev, [lineItemId]: '' }));
      fetchOrder();
    } catch (err) {
      toast({
        title: "Submission failed",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setUploadingItem(null);
    }
  };

  // Handle artwork deletion
  const handleDeleteArtwork = async (lineItemId) => {
    if (!window.confirm('Are you sure you want to delete this artwork?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/uploads/line-item/${orderId}/${lineItemId}/artwork`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Delete failed');
      }
      
      toast({
        title: "Artwork deleted",
        description: "You can now upload new artwork",
      });
      
      fetchOrder();
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err.message,
        variant: "destructive"
      });
    }
  };

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gold-600 animate-spin mx-auto mb-4" />
          <p className="text-ivory-500 font-body">Loading order...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !order) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <Card className="bg-ivory-100 border border-ivory-300/50 max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold text-ivory-800 mb-2">Order Not Found</h2>
            <p className="text-ivory-600 font-body mb-4">{error || "We couldn't find this order."}</p>
            <Button className="btn-luxury-primary" asChild>
              <Link to="/my-orders">Back to Orders</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const items = order.items || [];
  const shipping = order.shipping_address || {};

  return (
    <div className="min-h-screen bg-ivory-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to="/my-orders" 
            className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-4 transition-colors font-body"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Orders
          </Link>
          
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-semibold text-ivory-800">
                Order #{order.order_id}
              </h1>
              <p className="text-ivory-500 font-body">
                Placed on {new Date(order.created_at).toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric'
                })}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <StatusBadge status={order.status} type="order" />
              <StatusBadge status={order.payment_status} type="payment" />
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content - Order Items with Artwork */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="bg-ivory-100 border border-ivory-300/50">
              <CardHeader>
                <CardTitle className="font-heading text-ivory-800 flex items-center gap-2">
                  <Package className="w-5 h-5 text-gold-600" />
                  Order Items & Artwork
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {items.map((item, index) => {
                  const lineItemId = item.id || `li_${index}`;
                  const artwork = item.artwork;
                  const hasArtwork = artwork && (artwork.type === 'file' || artwork.type === 'link');
                  const FileIcon = artwork?.file_type ? getFileIcon(artwork.file_type) : File;
                  
                  return (
                    <div 
                      key={lineItemId} 
                      className="p-4 bg-ivory-50 rounded-xl border border-ivory-200"
                      data-testid={`line-item-${lineItemId}`}
                    >
                      {/* Item Info */}
                      <div className="flex gap-4 mb-4">
                        {item.image && (
                          <img 
                            src={item.image.startsWith('/') ? `${API_URL}${item.image}` : item.image}
                            alt={item.product_name}
                            className="w-16 h-16 object-cover rounded-lg"
                            onError={(e) => e.target.src = 'https://placehold.co/64x64/f5f3f0/9a8b7d?text=Product'}
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="font-medium text-ivory-800 font-body">{item.product_name}</h3>
                          <p className="text-sm text-ivory-500 font-body">
                            Qty: {item.quantity} × ${(item.unit_price || 0).toFixed(2)}
                          </p>
                          {item.sku && (
                            <p className="text-xs text-ivory-400 font-body">SKU: {item.sku}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gold-600 font-body">
                            ${((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Artwork Section */}
                      <div className="border-t border-ivory-200 pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-ivory-700 font-body">Artwork</h4>
                          {artwork && <StatusBadge status={artwork.status} type="artwork" />}
                        </div>
                        
                        {hasArtwork ? (
                          /* Show existing artwork */
                          <div className="bg-white p-3 rounded-lg border border-ivory-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-gold-500/10 flex items-center justify-center">
                                  {artwork.type === 'link' ? (
                                    <LinkIcon className="w-5 h-5 text-gold-600" />
                                  ) : (
                                    <FileIcon className="w-5 h-5 text-gold-600" />
                                  )}
                                </div>
                                <div>
                                  {artwork.type === 'file' ? (
                                    <>
                                      <p className="text-sm font-medium text-ivory-800 font-body">{artwork.file_name}</p>
                                      <p className="text-xs text-ivory-500 font-body">
                                        {(artwork.file_size / 1024 / 1024).toFixed(2)} MB · Uploaded {new Date(artwork.uploaded_at).toLocaleDateString()}
                                      </p>
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-sm font-medium text-ivory-800 font-body">
                                        {artwork.link_provider ? artwork.link_provider.replace('_', ' ').toUpperCase() : 'Cloud Link'}
                                      </p>
                                      <a 
                                        href={artwork.link_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-xs text-gold-600 hover:underline flex items-center gap-1 font-body"
                                      >
                                        Open link <ExternalLink className="w-3 h-3" />
                                      </a>
                                    </>
                                  )}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteArtwork(lineItemId)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* Show upload options */
                          <Tabs defaultValue="upload" className="w-full">
                            <TabsList className="grid w-full grid-cols-2 mb-3">
                              <TabsTrigger value="upload" className="text-sm">
                                <Upload className="w-4 h-4 mr-1" />
                                Upload File
                              </TabsTrigger>
                              <TabsTrigger value="link" className="text-sm">
                                <LinkIcon className="w-4 h-4 mr-1" />
                                Cloud Link
                              </TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="upload">
                              <div className="relative">
                                <input
                                  type="file"
                                  id={`file-${lineItemId}`}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  accept=".pdf,.ai,.eps,.svg,.png,.jpg,.jpeg,.tiff,.tif,.psd"
                                  onChange={(e) => handleFileUpload(lineItemId, e.target.files[0])}
                                  disabled={uploadingItem === lineItemId}
                                />
                                <div className="border-2 border-dashed border-ivory-300 rounded-lg p-4 text-center hover:border-gold-500 transition-colors">
                                  {uploadingItem === lineItemId ? (
                                    <div className="flex items-center justify-center gap-2">
                                      <Loader2 className="w-5 h-5 text-gold-600 animate-spin" />
                                      <span className="text-ivory-600 font-body">Uploading...</span>
                                    </div>
                                  ) : (
                                    <>
                                      <Upload className="w-8 h-8 text-ivory-400 mx-auto mb-2" />
                                      <p className="text-sm text-ivory-600 font-body">Click to upload artwork</p>
                                      <p className="text-xs text-ivory-400 font-body mt-1">
                                        PDF, AI, EPS, SVG, PNG, JPG, TIFF (max 50MB)
                                      </p>
                                    </>
                                  )}
                                </div>
                              </div>
                            </TabsContent>
                            
                            <TabsContent value="link">
                              <div className="space-y-2">
                                <Label className="text-xs text-ivory-600 font-body">
                                  Paste Google Drive, Dropbox, or other cloud link
                                </Label>
                                <div className="flex gap-2">
                                  <Input
                                    type="url"
                                    placeholder="https://drive.google.com/..."
                                    value={linkInputs[lineItemId] || ''}
                                    onChange={(e) => setLinkInputs(prev => ({ ...prev, [lineItemId]: e.target.value }))}
                                    className="bg-white border-ivory-300 text-ivory-800 font-body"
                                  />
                                  <Button
                                    onClick={() => handleLinkSubmit(lineItemId)}
                                    disabled={!linkInputs[lineItemId] || uploadingItem === lineItemId}
                                    className="btn-luxury-primary"
                                  >
                                    {uploadingItem === lineItemId ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      'Submit'
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </TabsContent>
                          </Tabs>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Order Summary */}
          <div className="space-y-4">
            {/* Totals */}
            <Card className="bg-ivory-100 border border-ivory-300/50">
              <CardHeader>
                <CardTitle className="font-heading text-ivory-800 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-gold-600" />
                  Order Total
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-ivory-600 font-body">
                  <span>Subtotal</span>
                  <span>${(order.subtotal || 0).toFixed(2)}</span>
                </div>
                {order.tax > 0 && (
                  <div className="flex justify-between text-ivory-600 font-body">
                    <span>Tax</span>
                    <span>${(order.tax || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-ivory-600 font-body">
                  <span>Shipping</span>
                  <span>${(order.shipping_cost || 0).toFixed(2)}</span>
                </div>
                <div className="border-t border-ivory-200 pt-3 flex justify-between text-lg font-semibold text-ivory-800 font-body">
                  <span>Total</span>
                  <span className="text-gold-600">${(order.total || 0).toFixed(2)}</span>
                </div>
                
                {order.is_pay_later && order.payment_status !== 'paid' && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 font-body">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Pay Later order - Invoice will be sent after artwork approval
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Shipping Address */}
            <Card className="bg-ivory-100 border border-ivory-300/50">
              <CardHeader>
                <CardTitle className="font-heading text-ivory-800 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-gold-600" />
                  Shipping To
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-ivory-600 font-body space-y-1">
                  <p className="font-medium text-ivory-800">{shipping.full_name}</p>
                  <p>{shipping.address_line1}</p>
                  {shipping.address_line2 && <p>{shipping.address_line2}</p>}
                  <p>{shipping.city}, {shipping.state} {shipping.zip_code}</p>
                  <p>{shipping.country || 'USA'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Help Text */}
            <div className="p-4 bg-gold-500/10 rounded-xl border border-gold-500/20">
              <h4 className="font-medium text-ivory-800 font-body mb-2">Need Help?</h4>
              <p className="text-sm text-ivory-600 font-body">
                Questions about your order? Contact us at{' '}
                <a href="mailto:support@engraveca.com" className="text-gold-600 hover:underline">
                  support@engraveca.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOrderDetailPage;
